import {
  Box,
  TextField,
  Button,
  Stack,
  IconButton,
  Typography
} from '@mui/material';
import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';
import { PhotoCamera, Delete as DeleteIcon } from '@mui/icons-material';
import { createHistoryItem, updateHistoryItem } from '../../services/api';

const HistoryForm = ({ initialValues, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    year: new Date().getFullYear(),
    order: 0
  });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (initialValues) {
      setFormData({
        title: initialValues.title || '',
        description: initialValues.description || '',
        year: initialValues.year || new Date().getFullYear(),
        order: initialValues.order || 0
      });
      if (initialValues.imageUrl) {
        const imageUrl = initialValues.imageUrl.startsWith('http')
          ? initialValues.imageUrl
          : `${process.env.REACT_APP_API_URL}${initialValues.imageUrl}`;
        setPreview(imageUrl);
      }
    }
  }, [initialValues]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setUploading(true);

      const data = new FormData();

      if (file) {
        data.append('image', file);
      }

      data.append('title', formData.title);
      data.append('description', formData.description);
      data.append('year', formData.year);
      data.append('order', formData.order);

      if (initialValues?.id) {
        await updateHistoryItem(initialValues.id, data);
      } else {
        await createHistoryItem(data);
      }

      onSuccess();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.message || 'Failed to save history item');
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      if (!selectedFile.type.startsWith('image/')) {
        toast.error('Only image files are allowed');
        return;
      }
      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Stack spacing={3}>
        <TextField
          name="title"
          label="Title"
          fullWidth
          required
          value={formData.title}
          onChange={handleInputChange}
        />

        <TextField
          name="description"
          label="Description"
          multiline
          rows={4}
          fullWidth
          required
          value={formData.description}
          onChange={handleInputChange}
        />

        <TextField
          name="year"
          label="Year"
          type="number"
          required
          value={formData.year}
          onChange={handleInputChange}
          inputProps={{ min: 1900, max: 2100 }}
        />

        <TextField
          name="order"
          label="Display Order"
          type="number"
          required
          value={formData.order}
          onChange={handleInputChange}
          inputProps={{ min: 0 }}
        />

        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Image
          </Typography>
          {preview && (
            <Box sx={{ mb: 2, position: 'relative', display: 'inline-block' }}>
              <img
                src={preview}
                alt="Preview"
                style={{ maxWidth: '200px', maxHeight: '120px' }}
              />
              <IconButton
                size="small"
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  bgcolor: 'background.paper'
                }}
                onClick={() => {
                  setFile(null);
                  setPreview(null);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          )}
          <Button
            variant="outlined"
            component="label"
            startIcon={<PhotoCamera />}
            sx={{ display: 'block' }}
          >
            Select Image
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleFileChange}
            />
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={uploading}
          >
            {initialValues?.id ? 'Update' : 'Create'}
          </Button>
          <Button
            variant="outlined"
            onClick={onCancel}
          >
            Cancel
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default HistoryForm;
